/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import {
    IBuyboxAddToCartViewProps,
    IBuyboxProductQuantityViewProps
} from '@msdyn365-commerce-modules/buybox/dist/types';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import { AsyncResult } from '@msdyn365-commerce/core';
import classnames from 'classnames';
import * as React from 'react';
import { ISmweBuyboxViewProps } from '../../../modules/smwe-buybox/./smwe-buybox';

const SmweBuyboxView: React.FC<ISmweBuyboxViewProps> = props => {
    const {
        ModuleProps,
        MediaGalleryContainerProps,
        ProductInfoContainerProps,
        addToCart,
        quantity,
        title,
        productDescription,
        productTitle,
        productPrice,
        productClubPrice,
        show,
        acclaimRatings,
        cookbookTagline,
        eventSchedule
        } = props;
    // @ts-ignore
    const tableClass: AsyncResult<string> = props.context.actionContext.get('String', 'Generic-SkuSelectorClasses');
    const isGiftCard = props.data.product && props.data.product.result ? props.data.product.result.ItemId?.toLowerCase() === 'giftcard' : false;

    return (
        <Module {...ModuleProps}>
            <Node {...MediaGalleryContainerProps}>{props.mediaGallery}</Node>
            <Node {...ProductInfoContainerProps}>
                {props.productApellation && (
                    <div>{props.productApellation}</div>
                )}
                {title}
                {productTitle}
                {productDescription}
                {eventSchedule}
                {acclaimRatings}
                {cookbookTagline}
                {show && _renderTable({
                    addToCart: addToCart,
                    quantity: quantity,
                    productPrice: productPrice,
                    productClubPrice: productClubPrice,
                    tableClass: tableClass,
                    isGiftCard: isGiftCard
                })}
                {props.content &&
                    props.content.map(
                        (contentSlotData: React.ReactNode, index: number) => {
                         return (
                                <React.Fragment key={index}>
                                    {contentSlotData}
                                </React.Fragment>
                            );
                        }
                    )}
            </Node>
        </Module>
    );
};

export interface IRenderTable {
    addToCart: IBuyboxAddToCartViewProps;
    quantity: IBuyboxProductQuantityViewProps | undefined;
    productPrice: React.ReactNode;
    productClubPrice: React.ReactNode;
    isGiftCard: boolean;
    tableClass: AsyncResult<string>;
}

const _renderTable = (props: IRenderTable) => {
    const { addToCart, quantity, productPrice, productClubPrice, tableClass, isGiftCard } = props;

    return (
        <div className={classnames('sku-selector', tableClass?.result)}>
            <div className='sku-selector__price'>
                {productPrice && (<><span className='sr-only'>Regular Price {productPrice}</span><div aria-hidden='true' className={classnames('sku-selector__price-default', { 'price-width-adjust': isGiftCard })}>{productPrice}</div></>)}
                {!isGiftCard && productClubPrice && (<><span className='sr-only'>Club Price {productClubPrice}</span><div aria-hidden='true' className='sku-selector__price-club'>{productClubPrice} Club</div></>)}
            </div>
            <div className='sku-selector__cart'>
                {quantity && <div className={classnames('sku-selector__cart-quantity', { unselectable: isGiftCard })}>{_renderQuantity(quantity)}</div>}
                {addToCart && <div className='sku-selector__cart-button'>{_renderAddToCart(addToCart)}</div>}
            </div>
        </div>
    );
};

const _renderAddToCart = (addToCart: IBuyboxAddToCartViewProps): JSX.Element => {
    const { ContainerProps, errorBlock, button } = addToCart;

    return (
        <Node {...ContainerProps}>
            {errorBlock}
            {button}
        </Node>
    );
};

const _renderQuantity = (quantity: IBuyboxProductQuantityViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, input, errors } = quantity;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>
                {errors}
            </Node>
            {input}
        </Node>
    );
};

export default SmweBuyboxView;
